import React, { useState, useEffect, useCallback, useRef } from 'react'
import { HiShieldCheck, HiVideoCamera, HiTrash, HiCheck, HiX, HiSparkles, HiCloudUpload, HiChip, HiFilm, HiCheckCircle, HiQuestionMarkCircle, HiLockClosed, HiLogin } from 'react-icons/hi'
import { api, ApiError } from '../utils/api'
import logger from '../utils/logger'
import {
  trackLlmProviderChanged, trackLlmModelChanged, trackLlmModelAdded,
  trackLlmModelDeleted, trackVideoUploaded, trackVideoSelected,
  trackVideoDeleted, trackAllVideosDeleted, trackDatabaseCleanup,
  trackErrorVideoUpload, trackErrorLlmConfig
} from '../utils/analytics'
import usePageTitle from '../hooks/usePageTitle'
import SkeletonLoader from './SkeletonLoader'
import { useAuth } from '../contexts/AuthContext'
import './AdminPage.css'

/**
 * VideoThumbnail component - Generates a thumbnail preview from a video URL
 * Uses HTML5 video element to capture a frame at ~1 second
 */
function VideoThumbnail({ videoUrl, filename, onDurationLoaded }) {
  const [thumbnailUrl, setThumbnailUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const generateThumbnail = useCallback(() => {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.preload = 'metadata'

    video.onloadeddata = () => {
      // Seek to 1 second or 10% of duration, whichever is smaller
      const seekTime = Math.min(1, video.duration * 0.1)
      video.currentTime = seekTime
      // Report duration
      if (video.duration && onDurationLoaded) {
        onDurationLoaded(filename, video.duration)
      }
    }

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 160
        canvas.height = 90 // 16:9 aspect ratio
        const ctx = canvas.getContext('2d')
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7)
        setThumbnailUrl(dataUrl)
        setIsLoading(false)
      } catch (err) {
        logger.warn('Failed to generate video thumbnail', { filename, error: err.message })
        setHasError(true)
        setIsLoading(false)
      }
    }

    video.onerror = () => {
      logger.warn('Failed to load video for thumbnail', { filename, videoUrl })
      setHasError(true)
      setIsLoading(false)
    }

    video.src = videoUrl
    video.load()

    // Cleanup function
    return () => {
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }, [videoUrl, filename, onDurationLoaded])

  useEffect(() => {
    const cleanup = generateThumbnail()
    return cleanup
  }, [generateThumbnail])

  const thumbnailStyles = {
    width: '80px',
    height: '45px',
    borderRadius: 'var(--radius-sm)',
    objectFit: 'cover',
    backgroundColor: 'var(--bg-tertiary)',
    flexShrink: 0
  }

  if (isLoading) {
    return (
      <div style={{
        ...thumbnailStyles,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse 1.5s ease-in-out infinite'
      }}>
        <HiFilm style={{ fontSize: '1.5rem', color: 'var(--text-secondary)', opacity: 0.5 }} />
      </div>
    )
  }

  if (hasError || !thumbnailUrl) {
    return (
      <div style={{
        ...thumbnailStyles,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed var(--border-color)'
      }}>
        <HiVideoCamera style={{ fontSize: '1.5rem', color: 'var(--text-secondary)' }} />
      </div>
    )
  }

  return (
    <img
      src={thumbnailUrl}
      alt={`Thumbnail for ${filename}`}
      style={thumbnailStyles}
      onClick={() => window.open(videoUrl, '_blank')}
      title="Click to open video"
    />
  )
}

function AdminPage() {
  usePageTitle('Admin')

  // Use auth context for role-based access
  const { user, profile, isAdmin, initializing } = useAuth()

  // Video selection from library (server-side storage for global effect)
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('')
  const [pendingVideoUrl, setPendingVideoUrl] = useState(null)
  const [videoDurations, setVideoDurations] = useState({})
  const [confirmingSelection, setConfirmingSelection] = useState(false)

  const [videoFile, setVideoFile] = useState(null)
  const [videoFiles, setVideoFiles] = useState([]) // For multiple uploads
  const [uploadingVideo, setUploadingVideo] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })
  const [uploadError, setUploadError] = useState('')
  const [hoveredVideo, setHoveredVideo] = useState(null) // For hover-based delete button
  const [showUploadTooltip, setShowUploadTooltip] = useState(false) // For upload info tooltip

  const [videos, setVideos] = useState([])
  const [loadingVideos, setLoadingVideos] = useState(true) // Start true for initial load
  const [deletingVideos, setDeletingVideos] = useState(false)
  const [llmConfigLoaded, setLlmConfigLoaded] = useState(false) // Track if LLM config has loaded

  const [cleaningDb, setCleaningDb] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  // LLM Provider selection
  const [llmProvider, setLlmProvider] = useState('gemini')
  const [savingProvider, setSavingProvider] = useState(false)

  // Unified model configuration for all providers
  const [providerModels, setProviderModels] = useState({
    gemini: { activeModel: '', models: [] },
    deepseek: { activeModel: '', models: [] },
    lmstudio: { activeModel: '', models: [] },
    fireworks: { activeModel: '', models: [] },
    togetherAi: { activeModel: '', models: [] },
    deepinfra: { activeModel: '', models: [] },
    openrouter: { activeModel: '', models: [] },
    groq: { activeModel: '', models: [] }
  })
  const [newModelInput, setNewModelInput] = useState('')
  const [addingModel, setAddingModel] = useState(false)
  const [deletingModel, setDeletingModel] = useState(null)

  // Refs for auto-scrolling to active items
  const videoLibraryRef = useRef(null)
  const modelsListRef = useRef(null)
  const [hoveredModel, setHoveredModel] = useState(null)

  useEffect(() => {
    // Load LLM provider preference from localStorage
    const savedProvider = localStorage.getItem('admin_llm_provider')
    if (savedProvider) {
      setLlmProvider(savedProvider)
    }
  }, [])

  // Fetch active video and LLM config from server when logged in
  useEffect(() => {
    const fetchActiveVideo = async () => {
      try {
        const response = await api.get('/api/admin/active-video')
        setSelectedVideoUrl(response.videoUrl || '')
        logger.info('Active video fetched', { videoUrl: response.videoUrl })
      } catch (err) {
        logger.error('Failed to fetch active video', { error: err.message })
      }
    }
    const fetchLLMConfig = async () => {
      try {
        const response = await api.get('/api/admin/llm-config')
        setProviderModels({
          gemini: { activeModel: response.gemini?.activeModel || '', models: response.gemini?.availableModels || [] },
          deepseek: { activeModel: response.deepseek?.activeModel || '', models: response.deepseek?.availableModels || [] },
          lmstudio: { activeModel: response.lmstudio?.activeModel || '', models: response.lmstudio?.availableModels || [] },
          fireworks: { activeModel: response.fireworks?.activeModel || '', models: response.fireworks?.availableModels || [] },
          togetherAi: { activeModel: response.togetherAi?.activeModel || '', models: response.togetherAi?.availableModels || [] },
          deepinfra: { activeModel: response.deepinfra?.activeModel || '', models: response.deepinfra?.availableModels || [] },
          openrouter: { activeModel: response.openrouter?.activeModel || '', models: response.openrouter?.availableModels || [] },
          groq: { activeModel: response.groq?.activeModel || '', models: response.groq?.availableModels || [] }
        })
        logger.info('LLM config fetched')
      } catch (err) {
        logger.error('Failed to fetch LLM config', { error: err.message })
      } finally {
        setLlmConfigLoaded(true)
      }
    }
    // Only fetch when user is admin
    if (isAdmin) {
      fetchActiveVideo()
      fetchLLMConfig()
    }
  }, [isAdmin])

  const handleProviderChange = (provider) => {
    setSavingProvider(true)
    try {
      localStorage.setItem('admin_llm_provider', provider)
      setLlmProvider(provider)
      trackLlmProviderChanged(provider)
      logger.info('LLM provider updated', { provider })
    } finally {
      setSavingProvider(false)
    }
  }

  const handleModelChange = async (provider, model) => {
    setSavingProvider(true)
    try {
      await api.post('/api/admin/llm-config', { provider, activeModel: model })
      setProviderModels(prev => ({
        ...prev,
        [provider]: { ...prev[provider], activeModel: model }
      }))
      trackLlmModelChanged(provider, model)
      logger.info('Active model updated', { provider, model })
    } catch (err) {
      logger.error('Failed to update model', { provider, error: err.message })
      trackErrorLlmConfig(provider, err.message)
      alert('Failed to update model: ' + err.message)
    } finally {
      setSavingProvider(false)
    }
  }

  const handleAddModel = async (provider) => {
    if (!newModelInput.trim()) return
    setAddingModel(true)
    try {
      const response = await api.request('/api/admin/llm-config', {
        method: 'PUT',
        body: JSON.stringify({ provider, model: newModelInput.trim() })
      })
      setProviderModels(prev => ({
        ...prev,
        [provider]: { ...prev[provider], models: response.availableModels || [] }
      }))
      setNewModelInput('')
      trackLlmModelAdded(provider, newModelInput.trim())
      logger.info('Model added', { provider, model: newModelInput.trim() })
    } catch (err) {
      logger.error('Failed to add model', { provider, error: err.message })
      trackErrorLlmConfig(provider, err.message)
      alert('Failed to add model: ' + err.message)
    } finally {
      setAddingModel(false)
    }
  }

  const handleDeleteModel = async (provider, model) => {
    if (!window.confirm(`Delete model "${model}"?`)) return
    setDeletingModel(model)
    try {
      const response = await api.request(`/api/admin/llm-config?provider=${provider}&model=${encodeURIComponent(model)}`, {
        method: 'DELETE'
      })
      const currentActive = providerModels[provider]?.activeModel
      setProviderModels(prev => ({
        ...prev,
        [provider]: {
          activeModel: currentActive === model && response.availableModels?.length > 0
            ? response.availableModels[0]
            : currentActive,
          models: response.availableModels || []
        }
      }))
      trackLlmModelDeleted(provider, model)
      logger.info('Model deleted', { provider, model })
    } catch (err) {
      logger.error('Failed to delete model', { provider, error: err.message })
      trackErrorLlmConfig(provider, err.message)
      alert('Failed to delete model: ' + err.message)
    } finally {
      setDeletingModel(null)
    }
  }

  // Removed: handleLogin - now using Supabase auth with role check
  // Removed: handleLogout - user signs out through the main app header

  // Video selection handlers
  const handleVideoSelect = (videoUrl) => {
    if (videoUrl !== selectedVideoUrl) {
      setPendingVideoUrl(videoUrl)
    } else {
      setPendingVideoUrl(null)
    }
  }

  const handleConfirmVideoSelection = async () => {
    if (!pendingVideoUrl) return
    setConfirmingSelection(true)
    try {
      await api.post('/api/admin/active-video', { videoUrl: pendingVideoUrl })
      setSelectedVideoUrl(pendingVideoUrl)
      setPendingVideoUrl(null)
      trackVideoSelected(pendingVideoUrl)
      logger.info('Active video updated', { videoUrl: pendingVideoUrl })
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to update active video'
      logger.error('Failed to update active video', { error: errorMessage })
      alert('Failed to update active video: ' + errorMessage)
    } finally {
      setConfirmingSelection(false)
    }
  }

  const handleCancelVideoSelection = () => {
    setPendingVideoUrl(null)
  }

  // Delete single video handler
  const [deletingSingleVideo, setDeletingSingleVideo] = useState(false)
  const handleDeleteSingleVideo = async (filename, videoPath) => {
    if (!window.confirm(`Delete "${filename}"? This action cannot be undone.`)) {
      return
    }
    setDeletingSingleVideo(true)
    try {
      await api.request(`/api/admin/videos/${encodeURIComponent(filename)}`, { method: 'DELETE' })
      logger.info('Video deleted', { filename })
      // Remove from local state
      setVideos(prev => prev.filter(v => v.filename !== filename))
      trackVideoDeleted(filename)
      // If this was the active video, clear it
      if (selectedVideoUrl === videoPath) {
        await api.post('/api/admin/active-video', { videoUrl: '' })
        setSelectedVideoUrl('')
      }
      // Clear pending if this was pending
      if (pendingVideoUrl === videoPath) {
        setPendingVideoUrl(null)
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to delete video'
      logger.error('Failed to delete video', { filename, error: errorMessage })
      trackErrorVideoUpload(filename, errorMessage)
      alert('Failed to delete video: ' + errorMessage)
    } finally {
      setDeletingSingleVideo(false)
    }
  }

  const handleDurationLoaded = useCallback((filename, duration) => {
    setVideoDurations(prev => ({ ...prev, [filename]: duration }))
  }, [])

  // Format duration in seconds to MM:SS
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Extract video format from filename
  const getVideoFormat = (filename) => {
    const ext = filename.split('.').pop()?.toUpperCase() || 'VIDEO'
    return ext
  }

  const validateVideoFile = (file) => {
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo']
    const maxSize = 50 * 1024 * 1024 // 50MB

    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only MP4, WebM, MOV, and AVI videos are allowed'
    }
    if (file.size > maxSize) {
      return 'File too large. Maximum size is 50MB'
    }
    return null
  }

  const handleVideoUpload = async (e) => {
    e.preventDefault()

    const filesToUpload = videoFiles.length > 0 ? videoFiles : (videoFile ? [videoFile] : [])

    if (filesToUpload.length === 0) {
      setUploadError('Please select at least one video file')
      logger.warn('Upload attempted with no files selected')
      return
    }

    // Validate all files first with detailed logging
    const validationResults = filesToUpload.map(file => {
      const error = validateVideoFile(file)
      if (error) {
        logger.warn('File validation failed', {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          error,
          hint: error.includes('type')
            ? 'Convert to MP4 or WebM format'
            : 'Compress video or split into smaller parts'
        })
      }
      return { file, error }
    })

    const invalidFiles = validationResults.filter(r => r.error)
    if (invalidFiles.length > 0) {
      const firstInvalid = invalidFiles[0]
      setUploadError(`${firstInvalid.file.name}: ${firstInvalid.error}`)
      return
    }

    setUploadingVideo(true)
    setUploadError('')

    const totalSize = filesToUpload.reduce((sum, f) => sum + f.size, 0)
    const batchStartTime = performance.now()

    logger.info('Starting batch video upload', {
      totalFiles: filesToUpload.length,
      totalSizeBytes: totalSize,
      totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2)
    })

    // Parallel upload with concurrency limit
    const CONCURRENT_LIMIT = 3
    const results = []
    let completedCount = 0

    // Helper function to upload a single file
    const uploadSingleFile = async (file, index) => {
      const startTime = performance.now()

      logger.info('Starting file upload', {
        fileName: file.name,
        fileSize: file.size,
        fileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
        fileType: file.type,
        index: index + 1,
        total: filesToUpload.length
      })

      try {
        const formData = new FormData()
        formData.append('video_file', file)

        await api.postForm('/api/admin/upload-video', formData)

        const duration = ((performance.now() - startTime) / 1000).toFixed(2)
        logger.info('File upload succeeded', {
          fileName: file.name,
          durationSeconds: duration,
          uploadSpeedMBps: (file.size / (1024 * 1024) / parseFloat(duration)).toFixed(2)
        })

        return { success: true, fileName: file.name }

      } catch (err) {
        const duration = ((performance.now() - startTime) / 1000).toFixed(2)
        const isApiError = err instanceof ApiError
        const errorMessage = isApiError ? err.message : 'Upload failed'
        const statusCode = isApiError ? err.statusCode : null

        // Determine actionable hint based on error type
        let hint = 'Try again or contact support'
        if (statusCode === 413 || errorMessage.includes('large')) {
          hint = 'Compress the video or split into smaller parts'
        } else if (statusCode === 415 || errorMessage.includes('type')) {
          hint = 'Convert to MP4 or WebM format'
        } else if (statusCode === 401 || statusCode === 403) {
          hint = 'Re-login to refresh your session'
        } else if (statusCode >= 500) {
          hint = 'Server error - try again later'
        } else if (!navigator.onLine || errorMessage.includes('network') || errorMessage.includes('fetch')) {
          hint = 'Check your internet connection'
        }

        logger.error('File upload failed', {
          fileName: file.name,
          fileSize: file.size,
          error: errorMessage,
          statusCode,
          durationSeconds: duration,
          hint,
          isNetworkError: !navigator.onLine
        })

        return { success: false, fileName: file.name, error: errorMessage, hint }
      }
    }

    // Process files in parallel batches with sliding window
    const pending = [...filesToUpload.map((file, idx) => ({ file, idx }))]
    const active = new Set()

    const processNext = async () => {
      if (pending.length === 0) return null

      const { file, idx } = pending.shift()
      const promise = uploadSingleFile(file, idx).then(result => {
        completedCount++
        setUploadProgress({ current: completedCount, total: filesToUpload.length })
        active.delete(promise)
        return result
      })
      active.add(promise)
      return promise
    }

    // Start initial batch
    const initialPromises = []
    for (let i = 0; i < Math.min(CONCURRENT_LIMIT, filesToUpload.length); i++) {
      initialPromises.push(processNext())
    }

    // Process remaining with sliding window
    while (pending.length > 0 || active.size > 0) {
      if (active.size > 0) {
        const completed = await Promise.race(active)
        results.push(completed)
        if (pending.length > 0) {
          processNext()
        }
      }
    }

    // Collect remaining results
    const remaining = await Promise.all(active)
    results.push(...remaining.filter(r => r))

    const batchDuration = ((performance.now() - batchStartTime) / 1000).toFixed(2)
    const successCount = results.filter(r => r?.success).length
    const failedResults = results.filter(r => r && !r.success)

    // Log batch completion with summary
    if (failedResults.length === 0) {
      logger.info('Batch upload completed successfully', {
        totalFiles: filesToUpload.length,
        successCount,
        durationSeconds: batchDuration,
        avgUploadSpeedMBps: (totalSize / (1024 * 1024) / parseFloat(batchDuration)).toFixed(2)
      })
    } else {
      logger.error('Batch upload completed with failures', {
        totalFiles: filesToUpload.length,
        successCount,
        failedCount: failedResults.length,
        failedFiles: failedResults.map(r => ({
          fileName: r.fileName,
          error: r.error,
          hint: r.hint
        })),
        durationSeconds: batchDuration
      })
    }

    // Clear file inputs
    setVideoFile(null)
    setVideoFiles([])
    const fileInput = document.getElementById('video-upload')
    if (fileInput) fileInput.value = ''

    // Show result
    if (failedResults.length === 0) {
      alert(`${successCount} video${successCount > 1 ? 's' : ''} uploaded successfully!`)
      trackVideoUploaded(successCount, totalSize)
    } else if (successCount > 0) {
      const failedNames = failedResults.map(r => r.fileName).join(', ')
      alert(`${successCount} uploaded, ${failedResults.length} failed: ${failedNames}`)
    } else {
      const failedNames = failedResults.map(r => r.fileName).join(', ')
      setUploadError(`All uploads failed: ${failedNames}. ${failedResults[0]?.hint || 'Try again later.'}`)
    }

    // Refresh the videos list
    fetchVideos()
    setUploadingVideo(false)
    setUploadProgress({ current: 0, total: 0 })
  }


  const fetchVideos = async () => {
    setLoadingVideos(true)

    try {
      logger.info('Fetching videos list')
      const response = await api.get('/api/admin/videos')
      setVideos(response)
      logger.info('Videos fetched successfully', { count: response.length })
    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to fetch videos list.'

      logger.error('Failed to fetch videos', { error: errorMessage })
      alert('Failed to load videos list: ' + errorMessage)
    } finally {
      setLoadingVideos(false)
    }
  }

  const handleDeleteAllVideos = async () => {
    if (!window.confirm('Are you sure you want to delete ALL uploaded videos? This action cannot be undone.')) {
      return
    }

    setDeletingVideos(true)

    try {
      logger.info('Deleting all videos')

      const response = await api.request('/api/admin/videos', { method: 'DELETE' })

      logger.info('All videos deleted successfully', { deletedCount: response.deleted.length })
      alert(response.message)

      // Refresh the videos list
      setVideos([])
      trackAllVideosDeleted(response.deleted.length)

      // Clear the active video since all videos are deleted
      try {
        await api.post('/api/admin/active-video', { videoUrl: '' })
        setSelectedVideoUrl('')
        setPendingVideoUrl(null)
      } catch (clearErr) {
        logger.warn('Failed to clear active video', { error: clearErr.message })
      }

    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to delete videos. Please try again.'

      logger.error('Video deletion failed', { error: errorMessage })
      alert('Video deletion failed: ' + errorMessage)
    } finally {
      setDeletingVideos(false)
    }
  }

  // Fetch videos when component mounts (after login)
  useEffect(() => {
    if (isAdmin) {
      fetchVideos()
    }
  }, [isAdmin])

  // Auto-scroll to active video when videos are loaded
  useEffect(() => {
    if (selectedVideoUrl && videos.length > 0 && videoLibraryRef.current) {
      const activeElement = videoLibraryRef.current.querySelector('[data-active-video="true"]')
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [selectedVideoUrl, videos])

  // Auto-scroll to active model when provider or models change
  useEffect(() => {
    const activeModel = providerModels[llmProvider]?.activeModel
    if (activeModel && modelsListRef.current) {
      const activeElement = modelsListRef.current.querySelector('[data-active-model="true"]')
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [llmProvider, providerModels])

  const handleCleanupConfirm = async () => {
    setShowConfirmDialog(false)
    setCleaningDb(true)

    try {
      logger.info('Starting database cleanup')

      await api.request('/api/admin/cleanup', { method: 'DELETE' })

      logger.info('Database cleanup completed successfully')
      trackDatabaseCleanup()
      alert('Database cleanup completed successfully!')

    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to clean database. Please try again.'

      logger.error('Database cleanup failed', { error: errorMessage })
      alert('Database cleanup failed: ' + errorMessage)
    } finally {
      setCleaningDb(false)
    }
  }

  // Show loading while auth is initializing
  if (initializing) {
    return <SkeletonLoader variant="admin-page" />
  }

  // Not logged in at all - show sign in message
  if (!user) {
    return (
      <div style={{ padding: '2rem 0' }} className="fade-in">
        <div className="card fade-in-up" style={{
          maxWidth: '400px',
          margin: '0 auto',
          animationDelay: '0.1s',
          animationFillMode: 'both',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '0.5rem',
            justifyContent: 'center'
          }}>
            <HiLogin style={{ fontSize: '1.75rem', color: 'var(--accent-primary)' }} />
            <h2 style={{ margin: 0 }}>Sign In Required</h2>
          </div>
          <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            Please sign in to access the admin panel.
          </p>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-tertiary)' }}>
            Use the sign in button in the header to authenticate.
          </p>
        </div>
      </div>
    )
  }

  // Logged in but not an admin - show access denied
  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem 0' }} className="fade-in">
        <div className="card fade-in-up" style={{
          maxWidth: '400px',
          margin: '0 auto',
          animationDelay: '0.1s',
          animationFillMode: 'both',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '0.5rem',
            justifyContent: 'center'
          }}>
            <HiLockClosed style={{ fontSize: '1.75rem', color: 'var(--error)' }} />
            <h2 style={{ margin: 0, color: 'var(--error)' }}>Access Denied</h2>
          </div>
          <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            You don&apos;t have admin privileges to access this panel.
          </p>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-tertiary)' }}>
            Signed in as: {user.email || profile?.display_name || 'Unknown user'}
          </p>
        </div>
      </div>
    )
  }

  // Show skeleton while initial data is loading after login
  const isInitialLoading = loadingVideos && !llmConfigLoaded
  if (isInitialLoading) {
    return <SkeletonLoader variant="admin-page" />
  }

  return (
    <div className="admin-container fade-in" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
      {/* Header Card */}
      <div className="card fade-in-up" style={{
        marginBottom: '1.5rem',
        animationDelay: '0.1s',
        animationFillMode: 'both'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <HiSparkles style={{ fontSize: '1.75rem', color: 'var(--accent-primary)' }} />
            <h2 style={{ margin: 0 }}>Admin Dashboard</h2>
          </div>
          <span style={{
            fontSize: '0.875rem',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <HiShieldCheck style={{ color: 'var(--success)' }} />
            {user.email || profile?.display_name || 'Admin'}
          </span>
        </div>
        <p style={{ margin: '0.75rem 0 0 0', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
          Manage application settings and database maintenance.
        </p>
      </div>

      {/* Admin Grid - 2 columns on laptop */}
      <div className="admin-grid">
        {/* AI Provider Configuration Card */}
        <div className="card fade-in-up" style={{
          animationDelay: '0.15s',
          animationFillMode: 'both'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <HiChip style={{ fontSize: '1.5rem', color: 'var(--accent-primary)' }} />
            <h3 style={{ margin: 0 }}>AI Provider Configuration</h3>
          </div>

          <p style={{
            marginBottom: '1rem',
            color: 'var(--text-secondary)',
            fontSize: '0.9rem',
            lineHeight: '1.6'
          }}>
            Select the AI provider for menu image analysis.
          </p>


          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            maxHeight: '520px',
            overflowY: 'auto',
            paddingRight: '0.5rem'
          }}>
            {/* Provider definitions */}
            {[
              { key: 'lmstudio', label: 'LM Studio', description: 'Local AI (requires LM Studio running)' },
              { key: 'gemini', label: 'Google Gemini', description: 'Cloud AI (requires API key on server)' },
              { key: 'deepseek', label: 'DeepSeek', description: 'Cloud AI (requires API key on server)' },
              { key: 'fireworks', label: 'Fireworks AI', description: 'Multi-model aggregator (requires API key on server)' },
              { key: 'togetherAi', label: 'Together.AI', description: 'Cloud AI (requires API key on server)' },
              { key: 'deepinfra', label: 'DeepInfra', description: 'Cloud AI (requires API key on server)' },
              { key: 'openrouter', label: 'OpenRouter', description: 'Multi-provider router (requires API key on server)' },
              { key: 'groq', label: 'Groq', description: 'Fast inference cloud AI (requires API key on server)' }
            ].map(provider => (
              <div key={provider.key}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1rem',
                  border: `2px solid ${llmProvider === provider.key ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: llmProvider === provider.key ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                  cursor: savingProvider ? 'wait' : 'pointer',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="radio"
                    name="llm-provider"
                    value={provider.key}
                    checked={llmProvider === provider.key}
                    onChange={() => handleProviderChange(provider.key)}
                    disabled={savingProvider}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      {provider.label}
                    </div>
                    <small style={{ color: 'var(--text-secondary)' }}>
                      {provider.description}
                    </small>
                  </div>
                </label>

                {/* Models Box - rendered inline under selected provider */}
                {llmProvider === provider.key && providerModels[llmProvider]?.models?.length > 0 && (
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '1rem',
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-color)'
                  }}>
                    {/* Models Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>
                      <HiChip style={{ fontSize: '1rem', color: 'var(--text-secondary)' }} />
                      <span style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                        Models ({providerModels[llmProvider]?.models?.length || 0})
                      </span>
                    </div>

                    {/* Models List - video library style */}
                    <div
                      ref={modelsListRef}
                      style={{
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: 'var(--bg-tertiary)',
                        marginBottom: '0.75rem',
                        maxHeight: '180px',
                        overflowY: 'auto'
                      }}
                    >
                      {providerModels[llmProvider]?.models?.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', margin: 0, textAlign: 'center', fontSize: '0.85rem', padding: '1rem' }}>
                          No models configured
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', padding: '0.5rem' }}>
                          {providerModels[llmProvider]?.models?.map((model) => {
                            const isActive = model === providerModels[llmProvider]?.activeModel
                            // Strip common prefixes for display
                            let displayName = model
                            if (llmProvider === 'fireworks') {
                              displayName = model.replace('accounts/fireworks/models/', '')
                            }
                            return (
                              <div
                                key={model}
                                data-active-model={isActive ? 'true' : undefined}
                                onClick={() => handleModelChange(llmProvider, model)}
                                onMouseEnter={() => setHoveredModel(model)}
                                onMouseLeave={() => setHoveredModel(null)}
                                style={{
                                  padding: '0.6rem 0.75rem',
                                  border: `2px solid ${isActive ? 'var(--success-color)' : 'var(--border-color)'}`,
                                  borderRadius: 'var(--radius-md)',
                                  backgroundColor: isActive ? 'rgba(74, 222, 128, 0.1)' : 'var(--bg-secondary)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  cursor: savingProvider ? 'wait' : 'pointer',
                                  transition: 'all 0.2s ease',
                                  position: 'relative'
                                }}
                              >
                                {/* Active badge */}
                                {isActive && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '8px',
                                    backgroundColor: 'var(--success-color)',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '0.6rem',
                                    fontWeight: '700',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Active
                                  </div>
                                )}

                                {/* Model name */}
                                <span style={{
                                  color: 'var(--text-primary)',
                                  fontSize: '0.8rem',
                                  fontWeight: isActive ? '600' : '400',
                                  wordBreak: 'break-all',
                                  flex: 1
                                }}>
                                  {displayName}
                                </span>

                                {/* Delete button - visible on hover */}
                                {hoveredModel === model && !isActive && providerModels[llmProvider]?.models?.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDeleteModel(llmProvider, model)
                                    }}
                                    disabled={deletingModel === model}
                                    style={{
                                      padding: '6px',
                                      backgroundColor: 'var(--error-color)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: deletingModel === model ? 'wait' : 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      opacity: deletingModel === model ? 0.6 : 1
                                    }}
                                    title="Delete model"
                                  >
                                    <HiTrash style={{ fontSize: '0.9rem' }} />
                                  </button>
                                )}

                                {/* Checkmark for active */}
                                {isActive && (
                                  <HiCheckCircle
                                    style={{
                                      fontSize: '1.25rem',
                                      color: 'var(--success-color)',
                                      flexShrink: 0
                                    }}
                                  />
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Add New Model */}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        value={newModelInput}
                        onChange={(e) => setNewModelInput(e.target.value)}
                        placeholder={llmProvider === 'fireworks' ? 'e.g. qwen2p5-vl-32b-instruct' : 'e.g. model-name'}
                        disabled={addingModel}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border-color)',
                          backgroundColor: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                          fontSize: '0.8rem'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleAddModel(llmProvider)}
                        disabled={addingModel || !newModelInput.trim()}
                        style={{
                          padding: '0.5rem 0.75rem',
                          borderRadius: 'var(--radius-sm)',
                          backgroundColor: 'var(--accent-primary)',
                          color: 'white',
                          border: 'none',
                          fontSize: '0.8rem',
                          cursor: !newModelInput.trim() ? 'not-allowed' : 'pointer',
                          opacity: !newModelInput.trim() ? 0.6 : 1
                        }}
                      >
                        {addingModel ? '...' : 'Add'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <small style={{
            color: 'var(--text-secondary)',
            fontSize: '0.8rem',
            marginTop: '0.75rem',
            display: 'block'
          }}>
            {{
              lmstudio: '⚡ Using local LM Studio server',
              gemini: '☁️ Using Google Gemini cloud API',
              deepseek: '🔮 Using DeepSeek cloud API',
              fireworks: '🚀 Using Fireworks AI multi-model API',
              togetherAi: '🤝 Using Together.AI cloud API',
              deepinfra: '🔧 Using DeepInfra cloud API',
              openrouter: '🌐 Using OpenRouter multi-provider API',
              groq: '⚡ Using Groq fast inference API'
            }[llmProvider] || '☁️ Using cloud AI'}
          </small>
        </div>

        {/* Video Management Card */}
        <div className="card fade-in-up" style={{
          animationDelay: '0.2s',
          animationFillMode: 'both'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <HiVideoCamera style={{ fontSize: '1.5rem', color: 'var(--accent-secondary)' }} />
            <h3 style={{ margin: 0 }}>Video Management</h3>
          </div>


          {/* Video Upload */}
          <div className="form-group">
            <label htmlFor="video-upload" className="form-label">
              Upload New Video
            </label>
            <form onSubmit={handleVideoUpload}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <input
                  id="video-upload"
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                  className="form-input"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || [])
                    if (files.length > 1) {
                      setVideoFiles(files)
                      setVideoFile(null)
                    } else {
                      setVideoFile(files[0] || null)
                      setVideoFiles([])
                    }
                    setUploadError('')
                  }}
                  disabled={uploadingVideo}
                  multiple
                  style={{ flex: 1, padding: '0.5rem' }}
                />
                <button
                  type="submit"
                  className={`btn btn-primary ${uploadingVideo ? 'btn-loading' : ''}`}
                  disabled={uploadingVideo || (!videoFile && videoFiles.length === 0)}
                  style={{ padding: '0.75rem 1rem', minWidth: '90px' }}
                >
                  <HiCloudUpload style={{ fontSize: '1rem' }} />
                  {uploadingVideo
                    ? `${uploadProgress.current}/${uploadProgress.total}`
                    : videoFiles.length > 1
                      ? `Upload ${videoFiles.length}`
                      : 'Upload'}
                </button>
              </div>
            </form>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                MP4, WebM, MOV, AVI - Select multiple files at once
              </small>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <HiQuestionMarkCircle
                  style={{
                    fontSize: '1rem',
                    color: 'var(--text-secondary)',
                    cursor: 'help',
                    opacity: 0.7,
                    transition: 'opacity 0.2s ease',
                    marginTop: '2px'
                  }}
                  onMouseEnter={() => setShowUploadTooltip(true)}
                  onMouseLeave={() => setShowUploadTooltip(false)}
                />
                {showUploadTooltip && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: '8px',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    fontSize: '0.8rem',
                    color: 'var(--text-primary)',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    animation: 'fadeIn 0.15s ease-out'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Upload Limits</div>
                    <div style={{ color: 'var(--text-secondary)' }}>
                      Recommended: 4.5MB, max 50MB.<br />
                      Files over 4.5MB may fail in production.
                    </div>
                    <div style={{
                      position: 'absolute',
                      bottom: '-6px',
                      left: '50%',
                      transform: 'translateX(-50%) rotate(45deg)',
                      width: '12px',
                      height: '12px',
                      backgroundColor: 'var(--bg-primary)',
                      borderRight: '1px solid var(--border-color)',
                      borderBottom: '1px solid var(--border-color)'
                    }} />
                  </div>
                )}
              </div>
            </div>
            {uploadError && (
              <div className="form-error" style={{ marginTop: '0.5rem' }}>
                {uploadError}
              </div>
            )}
          </div>

          {/* Video Library */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}>
            <HiFilm style={{ fontSize: '1.25rem', color: 'var(--text-secondary)' }} />
            <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
              Library ({videos.length})
            </span>
          </div>

          <div
            ref={videoLibraryRef}
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              padding: '0.75rem',
              backgroundColor: 'var(--bg-tertiary)',
              marginBottom: '1rem',
              maxHeight: '280px',
              overflowY: 'auto'
            }}
          >
            {loadingVideos ? (
              <div style={{ textAlign: 'center', padding: '0.5rem' }}>
                <div className="spinner spinner-small" style={{ margin: '0 auto' }}></div>
              </div>
            ) : videos.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', margin: 0, textAlign: 'center', fontSize: '0.9rem' }}>
                No videos uploaded yet
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {videos.map((video) => {
                  const isSelected = selectedVideoUrl === video.path
                  const isPending = pendingVideoUrl === video.path
                  return (
                    <div
                      key={video.filename}
                      data-active-video={isSelected && !isPending ? 'true' : undefined}
                      onClick={() => handleVideoSelect(video.path)}
                      onMouseEnter={() => setHoveredVideo(video.filename)}
                      onMouseLeave={() => setHoveredVideo(null)}
                      style={{
                        padding: '0.75rem',
                        border: `2px solid ${isPending
                          ? 'var(--accent-primary)'
                          : isSelected
                            ? 'var(--success-color)'
                            : 'var(--border-color)'
                          }`,
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: isSelected && !isPending
                          ? 'rgba(74, 222, 128, 0.1)'
                          : isPending
                            ? 'rgba(59, 130, 246, 0.1)'
                            : 'var(--bg-secondary)',
                        display: 'flex',
                        gap: '0.75rem',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}
                    >
                      {/* Active badge */}
                      {isSelected && !isPending && (
                        <div style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '8px',
                          backgroundColor: 'var(--success-color)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Active
                        </div>
                      )}

                      <VideoThumbnail
                        videoUrl={video.path}
                        filename={video.filename}
                        onDurationLoaded={handleDurationLoaded}
                      />

                      {/* Video info: format, size, duration */}
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{
                          backgroundColor: 'var(--accent-primary)',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          {getVideoFormat(video.filename)}
                        </span>
                        <span style={{
                          color: 'var(--text-secondary)',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {(video.size / (1024 * 1024)).toFixed(1)} MB
                        </span>
                        <span style={{
                          color: 'var(--text-secondary)',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {videoDurations[video.filename]
                            ? formatDuration(videoDurations[video.filename])
                            : '--:--'}
                        </span>
                      </div>

                      {/* Delete button - visible on hover */}
                      {hoveredVideo === video.filename && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteSingleVideo(video.filename, video.path)
                          }}
                          disabled={deletingSingleVideo}
                          style={{
                            padding: '6px',
                            backgroundColor: 'var(--error-color)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: deletingSingleVideo ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: deletingSingleVideo ? 0.6 : 1
                          }}
                          title="Delete video"
                        >
                          <HiTrash style={{ fontSize: '0.9rem' }} />
                        </button>
                      )}

                      {/* Selection indicator */}
                      {(isSelected || isPending) && (
                        <HiCheckCircle
                          style={{
                            fontSize: '1.5rem',
                            color: isPending ? 'var(--accent-primary)' : 'var(--success-color)',
                            flexShrink: 0
                          }}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Confirmation buttons when pending selection */}
          {pendingVideoUrl && (
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelVideoSelection}
                style={{ flex: 1, padding: '0.5rem' }}
              >
                <HiX style={{ fontSize: '1rem' }} />
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-success ${confirmingSelection ? 'btn-loading' : ''}`}
                onClick={handleConfirmVideoSelection}
                disabled={confirmingSelection}
                style={{ flex: 1, padding: '0.5rem' }}
              >
                <HiCheck style={{ fontSize: '1rem' }} />
                {confirmingSelection ? 'Saving...' : 'Confirm Selection'}
              </button>
            </div>
          )}

          {/* Clear selection button - when video is selected but no pending selection */}
          {selectedVideoUrl && !pendingVideoUrl && (
            <button
              type="button"
              className={`btn btn-secondary ${confirmingSelection ? 'btn-loading' : ''}`}
              onClick={async () => {
                setConfirmingSelection(true)
                try {
                  await api.post('/api/admin/active-video', { videoUrl: '' })
                  setSelectedVideoUrl('')
                  logger.info('Active video cleared')
                } catch (err) {
                  logger.error('Failed to clear active video', { error: err.message })
                  alert('Failed to clear selection')
                } finally {
                  setConfirmingSelection(false)
                }
              }}
              disabled={confirmingSelection}
              style={{ width: '100%', marginBottom: '0.5rem' }}
            >
              <HiX style={{ fontSize: '1rem' }} />
              {confirmingSelection ? 'Clearing...' : 'Clear Selection (Use Fallback Animation)'}
            </button>
          )}

          <button
            type="button"
            className={`btn btn-danger ${deletingVideos ? 'btn-loading' : ''}`}
            onClick={handleDeleteAllVideos}
            disabled={deletingVideos || videos.length === 0}
            style={{ width: '100%' }}
          >
            <HiTrash style={{ fontSize: '1rem' }} />
            {deletingVideos ? 'Deleting...' : `Delete All Videos (${videos.length})`}
          </button>
        </div>
      </div>

      {/* Database Maintenance Card - Full width below */}
      <div className="card fade-in-up" style={{
        marginTop: '1.5rem',
        animationDelay: '0.25s',
        animationFillMode: 'both'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem'
        }}>
          <HiTrash style={{ fontSize: '1.5rem', color: 'var(--error-color)' }} />
          <h3 style={{ margin: 0 }}>Database Maintenance</h3>
        </div>

        <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
          Clean up all database entries including polls, choices, and votes. This action cannot be undone.
        </p>

        <button
          type="button"
          className={`btn btn-danger ${cleaningDb ? 'btn-loading' : ''}`}
          onClick={() => setShowConfirmDialog(true)}
          disabled={cleaningDb}
          style={{ width: '100%' }}
        >
          <HiTrash style={{ fontSize: '1.25rem' }} />
          {cleaningDb ? 'Cleaning Database...' : 'Clean Database'}
        </button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowConfirmDialog(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: '400px',
              width: '90%',
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--glass-border)',
              boxShadow: 'var(--shadow-xl)',
              padding: '2rem',
              borderRadius: 'var(--radius-xl)',
              textAlign: 'center',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              color: 'var(--error-color)',
              fontSize: '3rem',
              marginBottom: '1rem'
            }}>
              <HiTrash />
            </div>

            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--error-color)' }}>
              Confirm Database Cleanup
            </h3>

            <p style={{
              marginBottom: '2rem',
              color: 'var(--text-secondary)',
              lineHeight: '1.6'
            }}>
              This will permanently delete all polls, choices, and votes from the database.
              This action cannot be undone. Are you sure you want to continue?
            </p>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirmDialog(false)}
                style={{ flex: 1 }}
              >
                <HiX style={{ fontSize: '1rem' }} />
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleCleanupConfirm}
                style={{ flex: 1 }}
              >
                <HiTrash style={{ fontSize: '1rem' }} />
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPage
